import React, { useEffect, useState } from "react";
import { Table, Select, DatePicker, Input, Space, Tag, Modal, Button } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";

const { RangePicker } = DatePicker;

const actorTypeColor = { admin: "blue", employee: "green", system: "default" };

const AuditLog = () => {
  const { request } = useRequest();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [detail, setDetail] = useState(null);

  const fetchLogs = () => {
    const params = new URLSearchParams();
    if (filters.actorType) params.append("actorType", filters.actorType);
    if (filters.action) params.append("action", filters.action);
    if (filters.targetType) params.append("targetType", filters.targetType);
    if (filters.from) params.append("from", filters.from);
    if (filters.to) params.append("to", filters.to);
    params.append("page", page);
    params.append("limit", 20);

    request({
      url: `${apiPath.auditLogs}?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        setLogs(res?.data?.logs || []);
        setTotal(res?.data?.total || 0);
      },
    });
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const columns = [
    { title: "When", dataIndex: "created_at", render: (v) => new Date(v).toLocaleString() },
    {
      title: "Actor Type",
      dataIndex: "actorType",
      render: (v) => <Tag color={actorTypeColor[v]}>{v}</Tag>,
    },
    { title: "Actor", dataIndex: ["actor", "name"], render: (v, r) => v || r.actor?.email || "-" },
    { title: "Action", dataIndex: "action" },
    { title: "Target Type", dataIndex: "targetType" },
    { title: "IP", dataIndex: "ip" },
    {
      title: "Details",
      render: (_, record) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(record)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader title="Audit Log" subtitle="Trace every admin, employee and system action" />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="Actor Type"
          style={{ width: 150 }}
          options={[
            { value: "admin", label: "Admin" },
            { value: "employee", label: "Employee" },
            { value: "system", label: "System" },
          ]}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, actorType: v }));
          }}
        />
        <Input
          allowClear
          placeholder="Action (e.g. employee.update)"
          style={{ width: 220 }}
          onChange={(e) => {
            setPage(1);
            setFilters((f) => ({ ...f, action: e.target.value || undefined }));
          }}
        />
        <RangePicker
          onChange={(dates, dateStrings) => {
            setPage(1);
            setFilters((f) => ({ ...f, from: dateStrings[0] || undefined, to: dateStrings[1] || undefined }));
          }}
        />
      </Space>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={logs}
        pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
        locale={{ emptyText: <EmptyState message="No audit log entries found" /> }}
      />

      <Modal title="Audit Log Detail" open={!!detail} onCancel={() => setDetail(null)} footer={null} width={720}>
        {detail && (
          <div>
            <p>
              <b>Action:</b> {detail.action}
            </p>
            <p>
              <b>Target:</b> {detail.targetType} {detail.targetId}
            </p>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <b>Before</b>
                <pre style={{ background: "#f5f5f5", padding: 8, maxHeight: 240, overflow: "auto" }}>
                  {JSON.stringify(detail.before, null, 2)}
                </pre>
              </div>
              <div>
                <b>After</b>
                <pre style={{ background: "#f5f5f5", padding: 8, maxHeight: 240, overflow: "auto" }}>
                  {JSON.stringify(detail.after, null, 2)}
                </pre>
              </div>
            </Space>
          </div>
        )}
      </Modal>
    </PageTransition>
  );
};

export default AuditLog;
